"use client"

import * as React from "react"

import { cn } from "@/lib/utils"

type RadioGroupContextValue = {
  name: string
  value: string
  onValueChange: (value: string) => void
  disabled?: boolean
}

const RadioGroupContext = React.createContext<RadioGroupContextValue | null>(
  null,
)

function RadioGroup({
  className,
  value,
  onValueChange,
  name,
  disabled,
  children,
}: {
  className?: string
  value: string
  onValueChange: (value: string) => void
  name?: string
  disabled?: boolean
  children: React.ReactNode
}) {
  const autoName = React.useId()
  return (
    <RadioGroupContext.Provider
      value={{
        name: name ?? autoName,
        value,
        onValueChange,
        disabled,
      }}
    >
      <div role="radiogroup" className={cn("grid gap-2", className)}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  )
}

function RadioGroupItem({
  className,
  value,
  children,
  disabled,
}: {
  className?: string
  value: string
  children: React.ReactNode
  disabled?: boolean
}) {
  const ctx = React.useContext(RadioGroupContext)
  if (!ctx) return null
  const isDisabled = disabled || ctx.disabled
  const id = `${ctx.name}-${value}`
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex cursor-pointer items-start gap-2 text-sm leading-snug",
        isDisabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      <input
        id={id}
        type="radio"
        name={ctx.name}
        value={value}
        checked={ctx.value === value}
        disabled={isDisabled}
        className="mt-0.5 size-3.5 shrink-0 accent-[var(--primary)]"
        onChange={() => ctx.onValueChange(value)}
      />
      <span className="min-w-0 flex-1">{children}</span>
    </label>
  )
}

export { RadioGroup, RadioGroupItem }
